<template>
  <div class="buybox__gallery">
    <div class="buybox__gallery-content">
      <div class="bg__thumbnails">
        <div class="bg__thumbnail-wrapper">
          <div v-for="(image,index) in carouselImages" :key="index" class="bg__thumbnail">
            <transition name="fade" mode="out-in">
              <ImageField :key="image.src" class="bg__thumbnail-image" :obj="{'url':image.src,'alt':image.alt,'width':128,'height':128}" width-param="width" sizes="64px" :isLoadingLazy="true"/>
            </transition>
          </div>
        </div> 
      </div>
      <div class="bg__images">
        <div v-if="isFeatureTag" class="bg__images-tag" :style=" { '--bg-tag': isFeatureTag.color }">
          {{ isFeatureTag.name }}
        </div>
        <div class="bg__slide-wrapper">
          <div v-for="(image,index) in carouselImages" :key="index" class="bg__slide">
            <span class="bg__image-count text-sm">{{index+1}} / {{carouselImages.length}}</span>
            <transition name="fade" mode="out-in">
              <ImageField :key="image.src" class="bg__image" width="800" :obj="{'url':image.src,'alt':image.alt,'width':1600,'height':1600}" width-param="width" sizes="(max-width: 800px) 100vw, 800px" :load="index === 0 ? true: false" :isLoadingLazy="true" @click="showLightbox(index)"/>
            </transition>
          </div>
        </div>
      </div>

      <VueEasyLightbox
        :visible="visible"
        :imgs="lightboxImgs"
        :index="index"
        @hide="visible = false"
        min-zoom="1"
      />
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, onMounted, watch, toRefs} from 'vue';
  import ImageField from "../../global/image.vue";
  import Swiper from 'swiper';
  import { Navigation, EffectFade, Thumbs } from 'swiper/modules';
  import VueEasyLightbox from 'vue-easy-lightbox'

  const props = defineProps({
    product: {
      type: Object,
      default: () => ({})
    },

    selectedVariant: {
      type: Object,
      default: () => ({})
    },

    isMobile: {
      type: Boolean,
      default: false, 
    },
  })

  const { product, selectedVariant, isMobile } = toRefs(props);
  const thumbnailSwiper = ref(null);
  const gallerySwiper = ref(null);

  onMounted(() => {
    initializeSwipers();
    initializeFrontrowMdBanner();
  });

  // onUpdated(() => {
  //   setTimeout(() => {
  //     destroySwipers();
  //     initializeSwipers();
  //   }, 100);
  // });

  const carouselImages = computed(() => {
    const { images, options } = product.value;
    
    // Extract first and second options
    const firstOption = (options.find(option => option.position === 1) || null);
    const secondOption = (options.find(option => option.position === 2) || null);

    // Build regex pattern based on firstOption and secondOption
    const imgPattern = imagePattern(firstOption, secondOption);

    // Filter images based on the regex pattern
    const matchingImages = images.filter(image => imgPattern.test(image.alt.replace(/&amp;/g, '&')));

    // Separate images with "last" in alt text
    const imagesWithLast = matchingImages.filter(image => /\/last$/i.test(image.alt));
    const imagesWithoutLast = matchingImages.filter(image => !/\/last$/i.test(image.alt));

    // Concatenate images with "last" at the end
    if (matchingImages.length > 0) {
      return [...imagesWithoutLast, ...imagesWithLast];
    } else if (selectedVariant.value.images.length > 0)  {
      return selectedVariant.value.images;
    } else {
      return product.value.images;
    }
  });

  const isFeatureTag = computed(() => {
    return selectedVariant.value.feature_tag;
  })

  const thumbnailCarousel = computed(() => {
    return {
      modules: [Navigation, EffectFade, Thumbs],
      slidesPerView: 6,
      spaceBetween: 12,
      direction: 'vertical',
      wrapperClass: 'bg__thumbnail-wrapper',
      slideClass: 'bg__thumbnail',
      threshold: 1000
    };
  });

  const galleryCarousel = computed(() => {
    return {
      modules: [Navigation, EffectFade, Thumbs],
      slidesPerView: 1.2,
      spaceBetween: 12,
      wrapperClass: 'bg__slide-wrapper',
      slideClass: 'bg__slide',
      thumbs: {
        swiper: thumbnailSwiper.value
      },
      breakpoints: {
        1024:{
          slidesPerView: 1,
          spaceBetween: 4
        } 
      },
    }
  })

  const initializeSwipers = () => {
    thumbnailSwiper.value = new Swiper('.bg__thumbnails', thumbnailCarousel.value);
    gallerySwiper.value = new Swiper('.bg__images', galleryCarousel.value);
    // gallerySwiper.value.slideTo(0, 500);
  };

  const destroySwipers = () => {
    thumbnailSwiper.value?.destroy();
    gallerySwiper.value?.destroy();
  };

  const imagePattern = (firstOption, secondOption) => {
    const firstOptionLower = firstOption.selected.toLowerCase();
    const secondOptionLower = secondOption?.selected.toLowerCase();
    const commonPatterns = [`^${firstOptionLower}/all$`, `^${firstOptionLower}/all/last$`, `^all$`];

    const regexPattern = secondOption && !['boxes', 'tubs', 'bottles'].includes(secondOption.name.toLowerCase())
    ? [`^${firstOptionLower}/${secondOptionLower}$`, `^${firstOptionLower}/${secondOptionLower}/last$`, ...commonPatterns]
    : [`^${firstOptionLower}$`, `^${firstOptionLower}/last$`, ...commonPatterns];

    return new RegExp(regexPattern.join('|'), 'i');
  }

  watch(carouselImages, () => {
    destroySwipers();
    initializeSwipers();
  })

  const lightboxImgs = carouselImages.value.map(img => img.src)

  const visible = ref(false)
  const index = ref(0)

  const showLightbox = (clickedIndex) => {
    index.value = clickedIndex
    visible.value = true
  }

  const initializeFrontrowMdBanner = () => {
    //Code provided by FrontrowMD
    const stickerPath = window.location.pathname;
    let scriptSrcSticker = '';
    let iframeSrcSticker = '';

    if (stickerPath.includes('/products/vitamin-d-k2')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3458";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3458";
      
    } else if (stickerPath.includes('/products/multivitamin-for-women-and-men')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3459";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3459";
    
    } else if (stickerPath.includes('/products/probiotics-for-women')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3448";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3448";
    
    } else if (stickerPath.includes('/products/organic-apple-cider-vinegar-pills')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3451";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3451";
    
    } else if (stickerPath.includes('/products/beauty-supplement-hair-skin-and-nails')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3449";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3449";
    
    } else if (stickerPath.includes('/products/plant-protein-for-lifelong-strength')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3462";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3462";
    
    } else if (stickerPath.includes('/products/probiotics-prebiotics-daily')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3437";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3437";
    
    } else if (stickerPath.includes('/products/plant-based-dha-omega-3')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3455";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3455";
    
    } else if (stickerPath.includes('/products/natural-glp-1-support')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3435";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3435";
    
    } else if (stickerPath.includes('/products/b-12')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3457";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3457";
    
    } else if (stickerPath.includes('/products/organic-superfood-green-powder')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3440";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3440";
    
    } else if (stickerPath.includes('/products/high-potency-probiotic-52-billion')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3453";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3453";
    
    } else if (stickerPath.includes('/products/performance-pre-workout')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3461";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3461";
    
    } else if (stickerPath.includes('/products/natural-sleep-aid-supplement')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3454";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3454";
    
    } else if (stickerPath.includes('/products/hormonal-balance-support-capsules')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3438";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3438";
    
    } else if (stickerPath.includes('/products/creatine-powder')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3436";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3436";
    
    } else if (stickerPath.includes('/products/break-it-down-digestive-enzymes-capsules')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3456";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3456";
    
    } else if (stickerPath.includes('/products/organic-protein-powder')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3434";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3434";
    
    } else if (stickerPath.includes('/products/plant-based-collagen-powder')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3444";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3444";
    
    } else if (stickerPath.includes('/products/organic-turmeric-golden-milk-powder')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3452";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3452";
    
    } else if (stickerPath.includes('/products/plant-based-vitamin-d-supplement')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3447";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3447";
    
    } else if (stickerPath.includes('/products/cognitive-support')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3441";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3441";
    
    } else if (stickerPath.includes('/products/turmeric-pills-with-black-pepper')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3446";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3446";
    
    } else if (stickerPath.includes('/products/advanced-electrolyte-hydration-drink-mix')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3463";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3463";
    
    } else if (stickerPath.includes('/products/stress-support')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3442";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3442";
    
    } else if (stickerPath.includes('/products/organic-probiotic-and-prebiotics-powder')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3443";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3443";
    
    } else if (stickerPath.includes('/products/4-in-1-magnesium-powder')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3439";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3439";
    
    } else if (stickerPath.includes('/products/organic-pre-workout-powder')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3445";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3445";
    
    } else if (stickerPath.includes('/products/probiotics-for-kids')) {
      scriptSrcSticker = "https://app.thefrontrowhealth.com/api/widgets/script?presentation_type=sticker&product_id=3460";
      iframeSrcSticker = "https://app.thefrontrowhealth.com/api/widgets?presentation_type=sticker&product_id=3460";
    
    }

    if (scriptSrcSticker && iframeSrcSticker) {
      const scriptSticker = document.createElement('script');
      scriptSticker.async = true;
      scriptSticker.src = scriptSrcSticker;
      document.head.appendChild(scriptSticker);

      const iframeSticker = document.createElement('iframe');
      iframeSticker.className = 'iframe-frontrow-sticker';
      iframeSticker.id = 'frontrow-sticker';
      
      iframeSticker.setAttribute('data-state', 'max');
      iframeSticker.setAttribute('frameborder', '0');
      iframeSticker.src = iframeSrcSticker;
      document.body.appendChild(iframeSticker);
      
      function placeSticker() { 
        const section = document.querySelector('.buybox__gallery .bg__slide.swiper-slide-active');
        if (section && iframeSticker.isConnected) {
            section.append(iframeSticker);
        }
      }

      requestAnimationFrame(() => requestAnimationFrame(placeSticker));
    }
  };
</script>